import {
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';

import { faBell } from '@fortawesome/free-solid-svg-icons';

import { AlertEditorComponent } from '../../alert-editor/alert-editor.component';

import {
  DialogCloseReason,
  DialogCloseOptions,
  injectDirtyCloseHandler,
} from '@sui/dialog';
import { SimpleDialogBase } from 'src/app/shared/abstract/simple-dialog-base';

import { Alert } from 'src/app/core/models/alert.models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface DialogData {
  alert: Alert;
}
interface DialogOutput {}

@Component({
  selector: 'app-alert-editor-dialog',
  templateUrl: './alert-editor-dialog.component.html',
  styleUrl: './alert-editor-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AlertEditorDialogComponent extends SimpleDialogBase<
  DialogData,
  DialogOutput
> {
  @ViewChild('alertEditor') alertEditor: AlertEditorComponent;

  protected destroyRef = inject(DestroyRef);

  protected dirtyHandler = injectDirtyCloseHandler(this.dialogRef);

  private readonly formValueChanges = signal<any>(null);
  private readonly formStatusChanges = signal<any>(null);

  public isDirty = computed(() => {
    this.formValueChanges();
    return this.alertEditor.formGroup.dirty;
  });

  public isValid = computed(() => {
    return this.formStatusChanges() === 'VALID';
  });

  get alert(): Alert {
    return this.dialogData.alert;
  }

  objectTitle = this.translateService.instant('popup.action-title.edit-alert');
  objectIcon = faBell;

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    const form = this.alertEditor.formGroup;

    form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((v) => this.formValueChanges.set(v));
    form.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((s) => this.formStatusChanges.set(s));

    this.formValueChanges.set(form.value);
    this.formStatusChanges.set(form.status);

    this.setupDirtyHandler(this.isDirty, this.isValid);
  }

  protected override performSave(): DialogCloseOptions<DialogOutput> {
    const formData = this.alertEditor.formGroup.value;

    this.alertEditor.submit();

    return {
      reason: DialogCloseReason.Save,
      data: formData,
    };
  }

  save(): void {
    if (!this.isValid()) {
      this.alertEditor.formGroup.markAllAsTouched();
      return;
    }

    const result = this.performSave();
    this.dirtyHandler.closeImmediately(result);
  }

  async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({
      reason: DialogCloseReason.Cancel,
    });
  }
}
